<!-- eslint-disable camelcase -->
<script setup lang="ts">
import {
  computed, onMounted, ref, watch, nextTick,
} from 'vue';
import { z } from 'zod';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import ConfirmPopup from 'primevue/confirmpopup';
import ConfirmDialog from 'primevue/confirmdialog';
import MultiSelect from 'primevue/multiselect';
import Step from 'primevue/step';
import StepItem from 'primevue/stepitem';
import StepPanel from 'primevue/steppanel';
import Stepper from 'primevue/stepper';
import Tag from 'primevue/tag';
import Textarea from 'primevue/textarea';
import Toast from 'primevue/toast';
import {
  useConfirmUnsavedChanges,
  useOpportunities,
  useOpportunityWizard,
  useRoutingHelpers,
  useStringFunctions,
  useToastNotifications,
  useUserPreferences,
  useUtilities,
  type UserPreferences,
  Selections,
} from '@apparatix/composables';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import { button as greenButtonTheme } from '@apparatix/theme/Green';
import {
  Column,
  Columns,
  FlexScroller,
  InputGroup,
  SplitBar,
  Stack,
  Toolbar,
  Typography,
} from '@apparatix/library/core';
import { ContactCard } from './ContactCard';
import LeadWizardContactForm from './LeadWizardContactForm.vue';
import {
  leadWizardState as baseSchema, LeadAndOpportunityModel, opportunityWizardState, wizardNewContact, type WizardNewContact,
} from '@apparatix/schemas/CreateLeadWizard';
import { createObjectFromSchema } from '@apparatix/schemas/Helpers';
import { PendingAccount, legacyAccountToStandard } from '@apparatix/schemas/Account';
import { PendingContact } from '@apparatix/schemas/Contact';
import CreateAccount from '@apparatix/library/CreateAccount.vue';
import OpportunityDetailForm from './OpportunityDetailForm.vue';
import { AccountTypes, OpportunityBase, OpportunityType } from '@apparatix/types/Leads';
import { useRouter } from 'vue-router';
import { AppStore } from '@apparatix/stores/AppStore';

const MUST_ADD_CONTACT_CONFIRM_GROUP = 'crm-leads-create-wizard:must-add-contact';
const FINISHED_TOAST_GROUP = 'crm-leads-create-wizard:finished';

type StepMeta = {
  badgeCount: number;
  badgeSeverity: 'secondary' | 'success' | 'danger';
  canProceed: boolean;
  tagLabel: string;
  tagSeverity: 'danger' | 'secondary' | 'success';
}

type CreateAccountMeta = {
  canSearch: boolean;
  canEdit: boolean;
  canCreateNew: boolean;
  submitLabel: string;
  cancelLabel: string;
  allowedTypes: string[];
  defaultType: 'Agency' | 'Advertiser';
}

type WizardSection = 'contacts' | 'accounts' | 'details';

type AutoAddedContactMeta = {
  addedAccount: boolean;
  contact?: WizardNewContact;
}


const emit = defineEmits<{
  (e: 'close', suppressUnsavedChangeWarning?: boolean): void;
  (e: 'on:loaded'): void;
  (e: 'update:has-unsaved-changes', value: boolean): void;
}>();

const finishButtonId = ref(`finish-button-${crypto.randomUUID()}`);

onMounted(async () => {
  emit('on:loaded');
  const userPrefs = await getPreferences();
  if (userPrefs) {
    preferences.value = userPrefs;
  }

  if (store.createOpportunityContext === 'convert-lead') {
    onFinish();
  }
});

const activeStep = ref(1);
const hasShownFinishToast = ref(false);
const topOfContactForm = ref<HTMLDivElement>();
const topOfAccountForm = ref<HTMLDivElement>();
const topOfDetailForm = ref<HTMLDivElement>();
const isLoading = ref(false);
const preferences = ref<UserPreferences>();
const createAccountComponent = ref();
const isEditingContact = ref(false);
const contactHasUnsavedEdits = ref(false);

const store = useOpportunityStore();

interface CreateLeadWizardProps {
  industryCodeRequired?: boolean;
  type: OpportunityType;
  editLead?: any;
}

const props = defineProps<CreateLeadWizardProps>();

const getSchema = (requireIndustryCode: boolean) => {
  const schemaWithCorrectType =
      props.type === 'Lead'
        ? baseSchema.extend({ type: z.literal(props.type) })
        : opportunityWizardState.extend({ type: z.literal(props.type) });

  if (!requireIndustryCode) return schemaWithCorrectType;

  const accountSchema = schemaWithCorrectType.shape.accounts;

  return schemaWithCorrectType.extend({
    accounts: accountSchema.superRefine((accounts, ctx) => {
      if (accounts.some(a => !a.industry && !a.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['accounts'],
          message: 'All Accounts must include an industry code',
        });
      }
    })
      .default(() => []),
  });
};

const getNewContactSchema = () => wizardNewContact;

const leadWizardState = getSchema(props.industryCodeRequired);

const getAccounts = () => {
  if (!(store.createOpportunityContext === 'convert-lead')) {
    return [];
  }

  return store.campaignAccounts.map(account =>
    legacyAccountToStandard.parse(account),
  );
};

const getContacts = () => {
  if (!(store.createOpportunityContext === 'convert-lead')) {
    return [];
  }

  return store.campaignContacts;
};

const state = ref<LeadAndOpportunityModel>(createObjectFromSchema(leadWizardState, {
  temperature: store.createOpportunityContext === 'convert-lead' && props.editLead ? props.editLead.opportunity.temperature : store.options[props.type].temperatures[0],
  status: store.options[props.type].availableStatuses[0],
  type: props.type,
  accounts: getAccounts(),
  contacts: getContacts(),
  id: store.createOpportunityContext === 'convert-lead' && props.editLead ? props.editLead.opportunity.id : undefined,
  name: store.createOpportunityContext === 'convert-lead' && props.editLead ? props.editLead.opportunity.name : undefined,
  owner: store.createOpportunityContext === 'convert-lead' && props.editLead ? { id: props.editLead.opportunity.ownerId, fullName: props.editLead.opportunity.owner } : store.currentUser,
  budget: store.createOpportunityContext === 'convert-lead' && props.editLead ? props.editLead.opportunity.value : undefined,
  source: store.createOpportunityContext === 'convert-lead' && props.editLead ? props.editLead.opportunity.source : undefined,
}));

const contactFormModel = ref<WizardNewContact>(createObjectFromSchema(getNewContactSchema()));

const { arrayHasValues, deepClone } = useUtilities();
const { isEmptyString } = useStringFunctions();
const { showError } = useToastNotifications();
const { openCrmDetailFlyout } = useRoutingHelpers();

const router = useRouter();

const {
  accountConflicts,
  activeEditingAccount,
  addAccount,
  addContact,
  addContactsFromAdvertiser,
  agencyAccountId,
  awaitingDuplicateAccountCheck,
  batchProcessModel,
  checkForAccountConflicts,
  getContactLabelChipSeverity,
  getUnusedContactLabels,
  hasAdvertiserAccount,
  hasAgencyAccount,
  primaryOrDefaultContact,
  removeAccount,
  removeContact,
  resolveAccountConflict,
} = useOpportunityWizard(state.value.accounts as PendingAccount[], state.value.contacts as PendingContact[], store.options['Lead'].contactLabels);

const { getPreferences } = useUserPreferences();
const { findUserById, onAfterLeadConverted } = useOpportunities();

const toast = useToast();
const confirm = useConfirm();

const contactsMeta = computed<StepMeta>(() => {
  const complete = arrayHasValues(state.value.contacts);

  return {
    badgeCount: state.value.contacts?.length ?? 0,
    badgeSeverity: complete ? 'success' : 'secondary',
    canProceed: complete,
    tagLabel: complete ? 'Complete' : 'Required',
    tagSeverity: complete ? 'success' : 'danger',
  };
});

const conflictInfo = computed(() => {
  if (!arrayHasValues(accountConflicts)) return undefined;
  return accountConflicts.value[0];
});

const accountsMeta = computed<StepMeta>(() => {
  const complete = arrayHasValues(state.value.accounts);
  const needsAttention = arrayHasValues(accountConflicts) || (arrayHasValues(state.value.accounts, { exactly: 1 }) && hasAgencyAccount());

  return {
    badgeCount: state.value.accounts?.length ?? 0,
    badgeSeverity: needsAttention
      ? 'danger'
      : complete ? 'success' : 'secondary',
    canProceed: true,
    tagLabel: needsAttention
      ? 'Needs Attention'
      : complete ? 'Complete' : props.type !== 'Lead' ? 'Required' : 'Optional',
    tagSeverity: needsAttention
      ? 'danger'
      : complete ? 'success' : props.type !== 'Lead' ? 'danger' : 'secondary',
  };
});

const createAccountMeta = computed<CreateAccountMeta>(() => {
  const advertiser = state.value.accounts.find(account => account.type === 'Advertiser');
  const agency = state.value.accounts.find(account => account.type === 'Agency');

  const advertiserIsTransient = Boolean(advertiser) && !advertiser?.id;
  const agencyIsTransient = Boolean(agency) && !agency?.id;
  const isEditing = Boolean(activeEditingAccount.value);

  const result: CreateAccountMeta = {
    canSearch: false,
    canEdit: false,
    canCreateNew: false,
    submitLabel: 'Create Account',
    cancelLabel: 'Cancel',
    allowedTypes: [],
    defaultType: 'Advertiser',
  };

  if (isEditing) {
    result.canEdit = true;
    result.submitLabel = `Update ${activeEditingAccount.value.name}`;
    result.defaultType = activeEditingAccount.value.type as 'Agency' | 'Advertiser';

    if (advertiser && !agency) {
      result.allowedTypes = ['Agency'];
    }

    if (agency && !advertiser) {
      result.allowedTypes = ['Advertiser'];
    }

    if (agency && advertiser) {
      result.allowedTypes = [activeEditingAccount.value.type];
    }

    return result;
  }

  // No Accounts added yet
  if (!arrayHasValues(state.value.accounts)) {
    result.canSearch = true;
    result.canCreateNew = true;
    result.allowedTypes = ['Agency', 'Advertiser'];
  }

  // Single account added
  if (arrayHasValues(state.value.accounts, { exactly: 1 })) {
    if (advertiser && advertiserIsTransient) {
      result.canCreateNew = true;
      result.canSearch = true;
      result.defaultType = 'Agency';
      result.allowedTypes = ['Agency'];
    }

    if (agency && agencyIsTransient) {
      result.canCreateNew = true;
      result.allowedTypes = ['Advertiser'];
    }

    if (agency && !agencyIsTransient) {
      result.canCreateNew = true;
      result.canSearch = true;
      result.allowedTypes = ['Advertiser'];
    }
  }

  return result;
});

const hasUnsavedChanges = computed(() => state.value.accounts?.length > 0 || state.value.contacts?.length > 0);

watch(() => hasUnsavedChanges.value, hasChanges => emit('update:has-unsaved-changes', hasChanges));

const { component: ConfirmCancel, onClick: onCancelWizardClick } = useConfirmUnsavedChanges(hasUnsavedChanges, () => {
  toast.removeGroup(FINISHED_TOAST_GROUP);
  emit('close');
});

const getLeadName = () => {
  if (primaryOrDefaultContact.value) {
    const name = primaryOrDefaultContact.value.fullName;
    const company = primaryOrDefaultContact.value.companyName;

    if (name && company) {
      return `${name} at ${primaryOrDefaultContact.value.companyName}`;
    }

    if (name && !company) {
      return name;
    }

    if (company && !name) {
      return company;
    }
  }
  return 'Unnamed';
};

const leadName = computed({
  get() {
    if (!isEmptyString(state.value.name, true)) {
      return state.value.name;
    }
    return getLeadName();
  },
  set(value: string) {
    state.value.name = value;
  },
});

const detailEditorSelections = computed<Selections>(() => ({
  owner: store.options[props.type].users,
  status: store.options[props.type].availableStatuses,
  temperature: store.options[props.type].temperatures,
  sources: store.options[props.type].accountSources.map(source => source.selection),
  office: store.options[props.type].offices,
}));

const useDynamicNameSearch = computed(() => {
  if (arrayHasValues(state.value.contacts) || arrayHasValues(state.value.accounts)) {
    return false;
  }

  // We need a strict type check here to distinguish between actually false vs missing/not yet loaded
  return typeof preferences.value?.crmCreateContactDynamicSearch === 'boolean'
    ? preferences.value.crmCreateContactDynamicSearch
    : true;
});

const useDynamicAccountSearch = computed(() => {
  if (arrayHasValues(state.value.accounts)) {
    return false;
  }

  // We need a strict type check here to distinguish between actually false vs missing/not yet loaded
  return typeof preferences.value?.crmCreateAccountDynamicSearch === 'boolean'
    ? preferences.value?.crmCreateAccountDynamicSearch
    : true;
});

const disableClosingWizard = computed(() => awaitingDuplicateAccountCheck.value || isLoading.value);

const editingContactHasExistingAccount = computed(() => {
  if (!contactFormModel.value?.isEditing || !arrayHasValues(state.value.accounts)) return false;

  return Boolean(state.value.accounts.find(account => account.contactKey === contactFormModel.value.key && account.id));
});

const checkIfValid = (): [valid: boolean, error?: string, path?: WizardSection] => {
  if (arrayHasValues(accountConflicts)) {
    return [false, `There are potential duplicate Accounts that need to be resolved before creating this ${props.type}`, 'accounts'];
  }
  try {
    leadWizardState.parse(state.value);
    return [true];
  } catch (e) {
    const [issue] = (e as z.ZodError).issues;
    if (import.meta.env.DEV) {
      console.error(`Error creating ${props.type}`, e);
    }
    return [false, issue.message, issue.path[0] as WizardSection];
  }
};

const showFinishToast = (contactName: string) => {
  toast.add({
    severity: 'success',
    group: FINISHED_TOAST_GROUP,
    summary: 'Success',
    detail: `'${contactName}' added as a Contact`,
  });

  hasShownFinishToast.value = true;

  setTimeout(() => toast.removeGroup(FINISHED_TOAST_GROUP), 15000);
};

const addPendingContact = async (contact: PendingContact) => {
  isEditingContact.value = false;

  if (!contact.id && state.value.owner?.id && state.value.owner.id !== store.currentUser.id) {
    contact.ownerId = state.value.owner.id;
  }

  await addContact(contact);

  if (shouldShowFinishToast() && !awaitingDuplicateAccountCheck.value) {
    const contactName = isEmptyString(contact.fullName, true) ? contact.companyName : contact.fullName;
    showFinishToast(contactName);
  }

  contactFormModel.value = createObjectFromSchema(getNewContactSchema());

  scrollToSection('contacts');
  createAccountComponent.value.reset();
};

watch(() => awaitingDuplicateAccountCheck.value, newValue => {
  if (!newValue && shouldShowFinishToast()) {
    const contactName = isEmptyString(primaryOrDefaultContact.value?.fullName, true) ? primaryOrDefaultContact.value.companyName : primaryOrDefaultContact.value.fullName;
    showFinishToast(contactName);
  }
});

watch(() => state.value?.owner, (newOwner, originalOwner) => {
  if (!newOwner || newOwner?.id === originalOwner?.id) return;

  state.value.accounts?.filter(account => !account.id)?.forEach(account => account.owner = newOwner);

  state.value.contacts?.filter(contact => !contact.id)?.forEach(contact => contact.ownerId = newOwner.id);
});

const onEditAccount = (account: PendingAccount) => {
  activeEditingAccount.value = deepClone(account);
};

const finishButton = ref(null);

const onCreateAccountSubmit = async (account: PendingAccount) => {
  if (activeEditingAccount.value) {
    const accountToEdit = state.value.accounts.find(a => a.key === account.key);
    const needsDuplicateCheck = accountToEdit.name.toLowerCase() !== account.name.toLowerCase();
    activeEditingAccount.value = undefined;

    if (needsDuplicateCheck && await checkForAccountConflicts(account)) {
      return;
    }
  }

  activeEditingAccount.value = undefined;

  if (!account.id && state.value.owner?.id && state.value.owner.id !== store.currentUser.id) {
    account.owner = state.value.owner;
  }

  if (account.owner && account.type !== 'Agency') {
    state.value.owner = deepClone(account.owner);
  }

  createAccountComponent.value.reset();
  await addAccount(account);

  finishButton.value.$el.click();
};

const onCreateAccountCancel = () => {
  activeEditingAccount.value = undefined;
  createAccountComponent.value.reset();
};

const addUnsavedContact = async (): Promise<AutoAddedContactMeta> => {
  if (isEditingContact.value) {
    return { addedAccount: false };
  }
  const contact = createObjectFromSchema(wizardNewContact);
  contact.ownerId = state.value.owner?.id ?? store.currentUser.id;

  const name = contactFormModel.value?.fullName;
  const companyName = contactFormModel.value?.account?.name ?? contactFormModel.value?.companyName;
  const companyType = contactFormModel.value?.account?.type ?? 'Advertiser';

  if (typeof name === 'string' && name.length >= 3) {
    contact.fullName = name;
  }

  if (arrayHasValues(contactFormModel.value.addresses)) {
    contact.addresses = contactFormModel.value.addresses;
  }

  if (arrayHasValues(contactFormModel.value.phoneNumbers)) {
    contact.phoneNumbers = contactFormModel.value.phoneNumbers;
  }

  if (arrayHasValues(contactFormModel.value.emails)) {
    contact.emails = contactFormModel.value.emails;
  }

  if (typeof companyName === 'string' && companyName.length >=3) {
    if (!arrayHasValues(state.value.accounts)) {
      contact.account.name = companyName;
      contact.account.type = companyType;
      contact.companyName = contact.account.name;
      contact.account.owner = state.value.owner ?? store.currentUser;
      if (arrayHasValues(contactFormModel.value.addresses)) {
        contact.account.addresses = contactFormModel.value.addresses;
      }
    } else {
      contact.companyName = companyName;
    }
  }


  if (arrayHasValues(state.value.contacts)) {
    const alreadyAdded = state.value.contacts.find(c => c.fullName === contact.fullName || c.companyName === contact.companyName);
    if (alreadyAdded) {
      return { addedAccount: false };
    }
  }

  if (contact.fullName || contact.companyName) {
    // Toast hasn't been shown, but this is so that it won't pop up since this function is only called when user clicks finish
    hasShownFinishToast.value = true;
    await addContact(contact, true);
  }

  return {
    addedAccount: Boolean(contact.account.name),
    contact,
  };
};

const onFinish = async (e?: MouseEvent) => {
  const confirmTarget = e?.currentTarget as HTMLElement ?? undefined;

  const contactMeta = await addUnsavedContact();

  if (contactMeta.addedAccount) {
    const hasConflicts = await checkForAccountConflicts(contactMeta.contact.account);
    if (hasConflicts) {
      activeStep.value = 2;
      scrollToSection('accounts');
      return;
    }
  }

  for (const account of state.value.accounts) {
    await addContactsFromAdvertiser(account.id);
  }

  const [canCreate, error, path] = checkIfValid();
  if (!canCreate) {
    showUnableToSaveMessage(error, path, confirmTarget);
    return;
  }

  if (isEmptyString(state.value.name, true)) {
    leadName.value = getLeadName();
  }

  toast.removeGroup(FINISHED_TOAST_GROUP);

  isLoading.value = true;

  try {
    const isConvertingLead = store.createOpportunityContext === 'convert-lead';
    const createdOpportunity = await batchProcessModel(state.value, props.type, isConvertingLead);
    isLoading.value = false;

    if (!createdOpportunity) {
      throw new Error(`Unable to save ${props.type}`);
    }

    if (isConvertingLead) {
      onAfterLeadConverted(state.value as OpportunityBase);
      emit('close');
      await nextTick();

      // Even though we could open the flyout directly here, this is a slight hack
      // to ensure the Lead flyout gets properly closed/unmounted since it was open
      // in the Leads context before the wizard opened up
      await router.push({ path: '/crm/leads' });
      await router.push({ path: '/crm/opportunities' });

      if (AppStore.activePage.label === 'Sales') {
        // Make sure the AppStore updates the active menu item in the sidebar
        const opportunityPage = AppStore.activeChildren.find(child => child.label === 'Opportunities');
        if (opportunityPage) {
          // true arg prevents the app store from calling any vue-router stuff
          AppStore.setActiveSubPage(opportunityPage, true);
        }
      }
      emit('close');
      await nextTick();
      await openCrmDetailFlyout('opportunities', 'opportunity', state.value.id, 'overview', false);
    } else {
      store.addOpportunity(createdOpportunity as OpportunityBase);
      emit('close');
    }
  } catch (err) {
    isLoading.value = false;
    showError(err, `Error creating ${props.type}`);
  }
};

const scrollToSection = (path: WizardSection) => {
  const scrollOptions: ScrollIntoViewOptions = {
    behavior: 'smooth',
    block: 'start',
  };
  switch (path) {
    case 'contacts':
      topOfContactForm.value.scrollIntoView(scrollOptions);
      break;
    case 'accounts':
      topOfAccountForm.value.scrollIntoView(scrollOptions);
      break;
    case 'details':
      topOfDetailForm.value.scrollIntoView(scrollOptions);
      break;
    default:
      topOfContactForm.value.scrollIntoView(scrollOptions);
  }
};

const shouldShowFinishToast = () => {
  if (awaitingDuplicateAccountCheck.value) return false;
  if (hasShownFinishToast.value) return false;
  if (!arrayHasValues(state.value.contacts)) return false;
  if (arrayHasValues(accountConflicts.value)) return false;
  if (hasAgencyAccount() && !hasAdvertiserAccount()) return false;
  return true;
};

const showUnableToSaveMessage = (error: string, openSection: WizardSection, target: HTMLElement) => {
  switch (openSection) {
    case 'contacts':
      activeStep.value = 1;
      break;
    case 'accounts':
      activeStep.value = 2;
      if (state.value.accounts.some(a => !a.industry && !a.id && props.industryCodeRequired)) {
        onEditAccount(state.value.accounts.find(a => !a.industry && !a.id));
      }
      break;
    case 'details':
      activeStep.value = 3;
      break;
    default:
      activeStep.value = 1;
  }

  // Todo: If primevue gets better it would be nice to use ConfirmPopup here rather than dialogue.
  // Right now the PV confirmation service can't process the target and will put in in the top left no
  // matter what is passed to it.
  const isPopup = false;

  confirm.require({
    ...(isPopup ? { target: target, position: 'topleft' } : {}),
    message: error,
    group: isPopup ? MUST_ADD_CONTACT_CONFIRM_GROUP : 'centered-error',
    acceptLabel: 'Ok',
    rejectClass: '!hidden',
    modal: false,
    acceptProps: {
      size: 'small',
      variant: 'outlined',
    },
    accept: () => {
      scrollToSection(openSection);
    },
  });
};

const onEditContactClick = (contact: WizardNewContact) => {
  isEditingContact.value = true;
  contactFormModel.value = { ...deepClone(contact), isEditing: true };
};

const onContactFormCancel = () => contactFormModel.value = createObjectFromSchema(getNewContactSchema());

const getToolTip = (account) => {
  // Prevent selecting Unqualified Advertiser from Create Opportunity
  if (account.type === 'Lead' && props.type === 'Opportunity') return 'You cannot select this Unqualified Advertiser until you have converted it.';

  return account.agencyId !== 0 && !state.value.accounts.some(acc => acc.id === account.agencyId) ? 'You cannot select this Account because it is under another agency.' : account.isAllowedSelection ? '' : `You cannot select this Account because it is owned by ${findUserById(account.owner?.id)?.fullName}`;
};
</script>
<template>
  <FlexScroller>
    <Stepper v-model:value="activeStep" class="px-2 lg:px-4 mt-3 lg:mt-6">
      <StepItem :value="1">
        <Step v-slot="{activateCallback}" as-child>
          <Toolbar align="left" class="py-2">
            <Toolbar
              align="left"
              class="cursor-pointer"
              role="button"
              @click="activateCallback"
            >
              <span class="p-step-number">1</span>
              <Typography variant="stepper-title">
                {{ props.type === 'Opportunity' ? 'Opportunity' : 'Contacts' }}
              </Typography>
            </Toolbar>
            <Badge :value="contactsMeta.badgeCount" :severity="contactsMeta.badgeSeverity" />
            <Tag :value="contactsMeta.tagLabel" :severity="contactsMeta.tagSeverity" />
          </Toolbar>
        </Step>
        <StepPanel class="my-3.5">
          <Columns>
            <Column>
              <div ref="topOfContactForm" />
              <LeadWizardContactForm
                v-model="contactFormModel"
                :is-opportunity-wizard="props.type === 'Opportunity'"
                :use-dynamic-company-search="useDynamicAccountSearch"
                :use-dynamic-name-search="useDynamicNameSearch"
                :is-associated-with-existing-account="editingContactHasExistingAccount"
                :allow-agency-in-wizard="store.options['Lead'].allowAgencyInWizard || store.createOpportunityContext === 'create-opportunity'"
                :hide-ok-cancel="true"
                @on:create="addPendingContact"
                @contact-selected="addPendingContact"
                @cancel="onContactFormCancel"
                @on:dirty-change="dirty => contactHasUnsavedEdits = dirty"
                @on:account-select="onCreateAccountSubmit"
              />
            </Column>
            <Column>
              <div v-if="arrayHasValues(state.contacts)" class="flex flex-col p-3 rounded-xl bg-[#F1F5F9]">
                <Stack :gap="4">
                  <ContactCard
                    v-for="contact in state.contacts"
                    :id="contact.key"
                    :key="contact.key"
                    :name="contact.fullName || 'Unnamed'"
                    :company="contact.companyName"
                    :owner="contact.ownerName ? contact.ownerName : findUserById(contact.ownerId)?.fullName ?? 'Unknown Owner'"
                    :closable="true"
                    @on-close="removeContact(contact)"
                  >
                    <Stack :gap="2">
                      <div class="flex items-center">
                        <MultiSelect
                          v-model="contact.contactLabels"
                          :options="getUnusedContactLabels(contact.key)"
                          option-label="contactLabelDisplayName"
                          placeholder="Select Contact Type"
                          display="chip"
                          size="small"
                          :show-toggle-all="false"
                          fluid
                        >
                          <template #chip="slotProps">
                            <Tag :severity="getContactLabelChipSeverity(slotProps.value)" :value="slotProps.value.contactLabelDisplayName" />
                          </template>
                        </MultiSelect>
                      </div>
                      <Button
                        v-show="! (Boolean(contact.id) || contact.key === contactFormModel?.key)"
                        label="Edit Contact"
                        size="small"
                        :disabled="Boolean(contact.id) || contact.key === contactFormModel?.key"
                        outlined
                        @click="onEditContactClick(contact)"
                      />
                    </Stack>
                  </ContactCard>
                </Stack>
              </div>
            </Column>
          </Columns>
        </StepPanel>
      </StepItem>
      <StepItem :value="2">
        <Step v-slot="{activateCallback}" as-child>
          <Toolbar align="left" class="py-2">
            <Toolbar
              align="left"
              class="cursor-pointer"
              role="button"
              @click="activateCallback"
            >
              <span class="p-step-number">2</span>
              <Typography variant="stepper-title">
                Accounts
              </Typography>
            </Toolbar>
            <Badge :value="accountsMeta.badgeCount" :severity="accountsMeta.badgeSeverity" />
            <Tag :value="accountsMeta.tagLabel" :severity="accountsMeta.tagSeverity" />
          </Toolbar>
        </Step>
        <StepPanel class="my-3.5">
          <div ref="topOfAccountForm" />
          <Columns v-if="!arrayHasValues(accountConflicts)">
            <Column>
              <CreateAccount
                ref="createAccountComponent"
                v-model="activeEditingAccount"
                :default-type="createAccountMeta.defaultType"
                :allowed-selection-types="(createAccountMeta.allowedTypes as AccountTypes[])"
                :disable-account-search="!createAccountMeta.canSearch"
                :allowed-creation-types="(createAccountMeta.allowedTypes as AccountTypes[])"
                :is-loading="isLoading || awaitingDuplicateAccountCheck"
                :disabled="!createAccountMeta.canCreateNew && !createAccountMeta.canEdit && !createAccountMeta.canSearch"
                :submit-button-label="createAccountMeta.submitLabel"
                :use-advertiser-search="Boolean(agencyAccountId)"
                :agency-id="agencyAccountId"
                :industry-code-required="props.industryCodeRequired"
                :allow-agency-in-wizard="store.options['Lead'].allowAgencyInWizard"
                hide-title
                externally-manage-account-limits
                show-cancel-button
                @on:account-selected="onCreateAccountSubmit"
                @on:submit="onCreateAccountSubmit"
                @on:cancel="onCreateAccountCancel"
              />
            </Column>
            <Column>
              <div v-if="arrayHasValues(state.accounts)" class="flex flex-col p-3 rounded-xl bg-[#F1F5F9]">
                <Stack :gap="4">
                  <ContactCard
                    v-for="account in state.accounts"
                    :id="account.key"
                    :key="account.key"
                    :name="account.legalName ?? account.name"
                    :company="account.displayType ?? account.type"
                    :owner="findUserById(account.owner?.id)?.fullName ?? 'Unknown Owner'"
                    :closable="true"
                    @on-close="removeAccount(account)"
                  >
                    <Button
                      v-show="! (Boolean(account.id) || account.key === activeEditingAccount?.key)"
                      label="Edit Account"
                      size="small"
                      :disabled="Boolean(account.id) || account.key === activeEditingAccount?.key"
                      outlined
                      @click="onEditAccount(account)"
                    />
                  </ContactCard>
                </Stack>
              </div>
            </Column>
          </Columns>
          <Columns v-if="arrayHasValues(accountConflicts)" class="lg:gap-x-8">
            <Column>
              <Stack :gap="5">
                <Stack :gap="2">
                  <Typography variant="section-heading">
                    <span class="pi pi-exclamation-circle" /> Potential duplicate Account
                  </Typography>
                  <Typography variant="body">
                    <span class="text-[var(--p-button-danger-background)]">{{ conflictInfo.pendingAccount.name }}</span>
                    might already exist. You&rsquo;ll either need to select one of the listed Accounts, or confirm that you wish to create {{ conflictInfo.pendingAccount.name }} as a new Account.
                  </Typography>
                </Stack>
                <Button label="Continue with new Account" @click="resolveAccountConflict(conflictInfo.pendingAccount.key)" />
              </Stack>
            </Column>
            <Column>
              <Stack :gap="5">
                <Typography variant="section-heading">
                  Existing Accounts with similar names
                </Typography>
                <div class="flex flex-col p-3 rounded-xl bg-[#F1F5F9]">
                  <Stack :gap="4">
                    <ContactCard
                      v-for="account in conflictInfo.conflicts"
                      :id="account.key"
                      :key="account.key"
                      :name="account.legalName ?? account.name"
                      :company="account.displayType ?? account.type"
                      :owner="findUserById(account.owner?.id)?.fullName ?? 'Unknown Owner'"
                    >
                      <div v-tooltip.left="getToolTip(account)" class="inline-flex">
                        <Button
                          label="Use this Account"
                          size="small"
                          :disabled="!account.isAllowedSelection || (account.agencyId !== 0 && !state.accounts.some(acc => acc.id === account.agencyId)) || (props.type === 'Opportunity' && account.type === 'Lead')"
                          outlined
                          @click="resolveAccountConflict(conflictInfo.pendingAccount.key, account.key)"
                        />
                      </div>
                    </ContactCard>
                  </Stack>
                </div>
              </Stack>
            </Column>
          </Columns>
        </StepPanel>
      </StepItem>
      <StepItem :value="3">
        <Step v-slot="{activateCallback}" as-child>
          <Toolbar align="left" class="py-2">
            <Toolbar
              align="left"
              class="cursor-pointer"
              role="button"
              @click="activateCallback"
            >
              <span class="p-step-number">3</span>
              <Typography variant="stepper-title">
                Details
              </Typography>
            </Toolbar>
            <Tag value="Optional" severity="secondary" />
          </Toolbar>
        </Step>
        <StepPanel class="my-3.5">
          <Stack :gap="5">
            <div ref="topOfDetailForm" />
            <OpportunityDetailForm
              v-model:name="leadName"
              v-model:owner="state.owner"
              v-model:status="state.status"
              v-model:source="state.source"
              v-model:temperature="state.temperature"
              v-model:type="state.type"
              v-model:value="state.value"
              v-model:due-date="state.dueDate"
              v-model:budget="state.budget"
              v-model:office="state.office"
              right-col-title="Notes"
              :selections="detailEditorSelections"
              :disable-status-change="false"
              :errors="{}"
              custom-right-col
            >
              <template #right-col>
                <InputGroup>
                  <Typography variant="form-label">
                    Optional Notes
                  </Typography>
                  <Textarea
                    v-model="state.notes"
                    rows="6"
                    fluid
                    auto-resize
                  />
                </InputGroup>
              </template>
            </OpportunityDetailForm>
          </Stack>
        </StepPanel>
      </StepItem>
    </Stepper>
    <template #footer>
      <SplitBar class="p-2 lg:p-4 border-t border-[var(--p-surface-200)]">
        <template #left>
          <Typography v-if="arrayHasValues(state.contacts)" variant="body">
            {{ leadName }}
          </Typography>
        </template>
        <template #right>
          <Toolbar>
            <Button
              severity="secondary"
              label="Cancel"
              :disabled="disableClosingWizard"
              @click="onCancelWizardClick"
            />
            <Button
              :id="finishButtonId"
              ref="finishButton"
              :label="isLoading ? 'Saving' : 'Finish'"
              :disabled="disableClosingWizard"
              :icon="isLoading ? 'pi pi-spin pi-spinner' : undefined"
              :dt="greenButtonTheme"
              @click="onFinish"
            />
          </Toolbar>
        </template>
      </SplitBar>
    </template>
  </FlexScroller>
  <Toast :group="FINISHED_TOAST_GROUP" @close="() => {}">
    <template #message="slotProps">
      <Stack :gap="2">
        <Typography variant="body">
          {{ slotProps.message.detail }}
        </Typography>
        <div>
          <Button
            :dt="greenButtonTheme"
            size="small"
            label="Finish"
            @click="onFinish"
          />
        </div>
      </Stack>
    </template>
  </Toast>
  <component :is="ConfirmCancel" />
  <ConfirmPopup :group="MUST_ADD_CONTACT_CONFIRM_GROUP" />
  <ConfirmPopup />
  <ConfirmDialog group="centered-error" />
</template>
<style scoped>
:deep(.p-steppanel-content) {
  border-left: 2px solid var(--p-stepper-separator-background);
}
</style>
